import _pick from 'lodash/pick';
import _get from 'lodash/get';
import {
  GeojsonFeatureCollectionInputObject,
  SiteCreateInputObject,
  SiteUpdateInputObject,
} from 'graphql-common';
import { Values } from '@lib/interfaces/form';
import getPhoneNumberPreparedDataFromValues from '@lib/utils/getPhoneNumberPreparedDataFromValues';
import { SelectOption } from '@lib/components/Select/types';
import mergeAndUnlinkMultiSelectValues from '@lib/utils/mergeAndUnlinkMultiSelectValues';
import { SiteFieldNames } from '@lib/enums/fieldNames/siteFieldNames';
import removeTypenameDeep from '@lib/utils/removeTypenameDeep';
import removePropertiesTypeDeep from '@lib/utils/removePropertiesTypeDeep';
import { addWhiteToIconUrl } from 'routes/Sites/utils/addWhiteToIconUrl';

export default function getSitePreparedDataFromValues(
  values: Values,
  prevValues: Values,
) {
  const newValues = _pick(values, [
    'code',
    'description',
    'email',
    'industry',
    'name',
  ]);
  const phoneNumber = getPhoneNumberPreparedDataFromValues(
    values,
    SiteFieldNames.PhoneNumber,
  );
  const conductCodeSections = _get(values, SiteFieldNames.ConductCodeSections);
  let newConductCodeSections;
  if (conductCodeSections && Array.isArray(conductCodeSections)) {
    newConductCodeSections = conductCodeSections.length
      ? conductCodeSections.map((item, index) => ({
          title: _get(item, SiteFieldNames.Title),
          body: _get(item, SiteFieldNames.Body),
          position: index + 1,
        }))
      : [];
  }

  const areas = _get(values, SiteFieldNames.Areas);
  let newAreas;
  if (areas && Array.isArray(areas)) {
    newAreas = areas.length ? areas : undefined;
  }

  let locationPhotoAttached;
  if (_get(values, SiteFieldNames.LocationPhotoAttached) === null) {
    locationPhotoAttached = { _destroy: true };
  }
  if (_get(values, [SiteFieldNames.LocationPhotoAttached, 'file', 'id'])) {
    locationPhotoAttached = {
      file: _get(values, [SiteFieldNames.LocationPhotoAttached, 'file']),
      filename: _get(values, [
        SiteFieldNames.LocationPhotoAttached,
        'filename',
      ]),
    };
  }

  const sectorId = _get(values, [SiteFieldNames.Sector, 'value']);

  const assigneesValue = (_get(values, SiteFieldNames.UserMemberships) ||
    []) as SelectOption[];
  const prevAssigneesValue = (_get(
    prevValues,
    SiteFieldNames.UserMemberships,
  ) || []) as SelectOption[];
  const members = assigneesValue.map(({ value }) => ({
    id: `${value}`,
  }));
  const prevMembers = prevAssigneesValue.map(({ value }) => ({
    id: `${value}`,
  }));
  const userMemberships = mergeAndUnlinkMultiSelectValues(prevMembers, members);

  const address = _get(values, SiteFieldNames.Address, null) as string;
  const locationPoint = _get(
    values,
    SiteFieldNames.LocationPoint,
  ) as GeojsonFeatureCollectionInputObject;
  const pointerShape = _get(values, [SiteFieldNames.PointerShape, 'value']);
  const pointerColor = _get(values, [SiteFieldNames.PointerColor, 'value']);
  const pointerIcon = _get(values, [
    SiteFieldNames.PointerIconAttached,
    'fileTag',
  ]);

  if (locationPoint && Array.isArray(locationPoint.features)) {
    for (let i = 0; i < locationPoint.features.length; i += 1) {
      const feature = locationPoint.features[i];
      if (feature) {
        const props = feature.properties || {};
        props.addressDetails = address;
        if (pointerShape) {
          props.shape = pointerShape;
        }
        if (pointerColor) {
          props.color = pointerColor.replace('chip-', '');
        }
        if (pointerIcon) {
          props.icon = addWhiteToIconUrl(pointerIcon);
        }
        feature.properties = props;
      }
    }
  }

  return {
    ...newValues,
    areas: newAreas,
    userId: _get(values, 'userId.value'),
    conductCodeSections: newConductCodeSections,
    locationPhotoAttached,
    phoneNumber,
    sectorId: sectorId || null,
    userMemberships,
    locationPoint: locationPoint
      ? {
          geojson: removePropertiesTypeDeep(removeTypenameDeep(locationPoint)),
        }
      : undefined,
  } as SiteCreateInputObject | SiteUpdateInputObject;
}
